/**
 * RUNNING ACCOUNT BILLS — the register, what is ready to bill, and one bill.
 *
 * A running account bill is CUMULATIVE. It states everything certified to date
 * and subtracts every earlier approved bill, so this bill is the difference.
 * That is not an accounting habit: it is what makes the whole thing
 * self-correcting. A deduction certified in the Measurement Book after RA-1 has
 * gone out simply makes RA-2 smaller. Nobody raises a credit note and nobody
 * argues about which bill was wrong.
 *
 * The consequence is on the screen, and is meant to be: A BILL MAY BE NEGATIVE.
 * When it is, this screen says so in plain words rather than hiding it, because
 * an Accounts clerk who sees a bill for minus forty thousand and has not been
 * told why will telephone somebody.
 *
 * NOBODY TYPES A QUANTITY HERE. There is no field for one. Every figure comes
 * from certified measurement pages, the terms agreed once on the work order,
 * and the contractor's PAN.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  AlertTriangle, ArrowLeft, Banknote, Calculator, FileDown, FileSignature,
  FilterX, Landmark, Lock, Plus, Receipt, Ruler, Scale, TrendingUp, Wallet, Flag, Undo2,
  Trash2,
} from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { rcmSupplierClause } from '../lib/reverse-charge'
import { dbMessage, usePeople, useQuery, useSites } from '@/lib/data'
import { downloadFromFunction, supabase } from '@/lib/supabase'
import { ddmmyyyy, qty, rupees } from '@/lib/format'
import {
  Alert, Card, EmptyState, Field, Input, Loading, Modal, SearchInput, Select,
  Stat, StatusPill, Textarea, Th, sortRows, useSort, useToast,
} from '@/components/ui'
import { AuditTrail, saveAttachment } from '@/components/docs/shared'
import {
  listWoPapers, uploadWoPaper, woPaperViewUrl, WO_PAPER_BUCKET, type WoPaper,
} from '../lib/papers'
import { ApprovalChain } from '../components/ApprovalChain'
import { RaiseCreditNote } from './CreditNotes'
import { ContractorInvoices } from '../components/ContractorInvoices'
import { RaPayments } from '../components/RaPayments'
import { serviceCan } from '../nav'

const n = (v: unknown) => Number(v ?? 0) || 0

export interface RaRow {
  id: string; wo_id: string; site_id: string; site_name: string
  wo_title: string | null; contract_value: number
  vendor_id: string; vendor_name: string | null; vendor_pan: string | null
  status: string; current_step: number
  ra_no: number; is_final: boolean
  bill_date: string; period_from: string | null; period_to: string | null
  cum_value: number; prev_value: number; gross_value: number
  gst_treatment: string; gst_rate_percent: number; gst_amount: number
  is_reverse_charge: boolean; rcm_gst_amount: number
  is_gst_registered: boolean
  rcm_notification: string | null; rcm_entry_no: string | null
  retention_pct: number; retention_amount: number; cum_retention: number
  tds_pct: number | null; tds_section: string | null; tds_amount: number
  advance_recovered: number; cum_advance_recovered: number; advance_outstanding: number
  other_deductions: number; other_deduction_note: string | null
  net_payable: number; payable_id: string | null
  payable_status: string | null
  advance_paid: number; advance_pct: number; advance_recovery_pct: number
  paid_to_date: number; unpaid_balance: number; payment_count: number
  created_by: string | null; creator_email: string | null
  approver_email: string | null; approval_date: string | null
  return_reason: string | null; rejection_reason: string | null
  remarks: string | null; date_created: string
  line_count: number; pct_of_contract: number | null
}

export interface RaLine {
  id: string; ra_id: string; wo_line_id: string; sl_no: number
  description: string | null; uom: string | null; rate: number
  qty_cum: number; qty_prev: number; qty_this: number
  value_cum: number; value_prev: number; value_this: number
}

// ---------------------------------------------------------------------------
// THE REGISTER, with "ready to bill" above it
// ---------------------------------------------------------------------------
const BLANK = { term: '', status: '', site: '' }

/**
 * THE ORDERS HELD BY A BILL THAT IS STILL OPEN — 0173.
 *
 * Drawn in both places a Project Manager can find himself blocked: under the
 * "certified, not yet billed" table, and inside the nothing-is-ready panel,
 * which is where he lands when the open bill is the ONLY reason the list is
 * empty. It was that second case that made the old wording so expensive — the
 * screen was blank, the explanation was one clause of one sentence, and the
 * bill he needed to deal with was never named.
 *
 * Each row is the order, the bill, and the one thing to do about it. The verb
 * depends on where the bill has got to, because the remedy does:
 *
 *   DRAFT / RETURNED  -> it is his. Finish it, or withdraw it.
 *   SUBMITTED         -> it is the approver's. Nobody else may take it back.
 */
function HeldByAnOpenBill({ held }: { held: any[] }) {
  return (
    <div className="mt-3 rounded-md border border-slate-200 bg-white px-3 py-2">
      <p className="text-xs font-medium text-slate-600">
        {held.length === 1 ? 'One order is' : `${held.length} orders are`} not listed
        because a bill is still open on {held.length === 1 ? 'it' : 'them'}
      </p>
      <p className="mt-0.5 text-[11px] leading-snug text-slate-400">
        Only one bill may be open on an order at a time, or two of them would claim the same
        certified work. Finish it or put it down, and the order comes back to this list.
      </p>
      <ul className="mt-2 space-y-1">
        {held.map((h: any) => (
          <li key={h.wo_id}
              className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-slate-600">
            <Link to={`/service/work-orders/${h.wo_id}`}
                  className="font-medium text-amber-700 hover:underline">{h.wo_id}</Link>
            <span className="text-slate-300">·</span>
            {h.bill ? (
              <>
                <Link to={`/service/ra-bills/${h.bill.id}`}
                      className="font-medium text-amber-700 hover:underline">
                  RA-{h.bill.ra_no}
                </Link>
                <StatusPill status={h.bill.status} />
                <span className="text-slate-500">
                  {h.bill.status === 'SUBMITTED'
                    ? '— with its approver. He returns it or approves it; it cannot be taken back from his queue.'
                    : '— open it to finish it, or to withdraw it and free the order.'}
                </span>
              </>
            ) : (
              // Belt and braces. The register and the position view are two
              // reads, and a bill raised between them would land here.
              <span className="text-slate-500">a bill is open on it — open the order to see it</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

export function RaBillList() {
  const { profile } = useAuth()
  const people = usePeople()
  const toast = useToast()
  const { data: allSites } = useSites()
  const [f, setF] = useState({ ...BLANK })
  const [busy, setBusy] = useState(false)
  const { sort, toggle } = useSort('date_created', 'desc')

  const set = (patch: Partial<typeof BLANK>) => setF((v) => ({ ...v, ...patch }))
  const dirty = Object.entries(f).some(([k, v]) => v !== (BLANK as any)[k])

  const { data: rows, loading, refresh } = useQuery<RaRow>(
    'v_ra_bill_register',
    () => supabase.from('v_ra_bill_register').select('*'),
    [], { cacheable: false })

  /** Certified but not yet billed, per order. The prompt to raise the next one. */
  const { data: position, refresh: refreshPos } = useQuery<any>(
    'v_wo_billing_position',
    () => supabase.from('v_wo_billing_position').select('*'),
    [], { cacheable: false, idField: 'wo_id' })

  // Board 16-09-2026: the site's Project Manager or the Project Head.
  const canBill = serviceCan(profile?.role, 'prepareRaBill')

  /*
    WORK CERTIFIED AND NOT YET BILLED, split three ways — 0162.

    Reported 20-09-2026 as "the Create RA Bill button has disappeared". It had
    not: nothing was ready, every order had value_certified = value_billed,
    and this panel hides itself when empty. An empty panel and a missing
    feature look identical from a chair.

    The split matters because "not ready" has two quite different meanings. A
    bill already open is a normal, temporary state. A closed order, or one
    whose final bill has been declared, is permanent — and the triggers added
    in 0156 and 0157 will REFUSE a bill there, so offering a button would
    throw and hiding the row would make outstanding certified work vanish.
    v_wo_billing_position now says which, in cannot_bill_because.
  */
  const outstanding = useMemo(
    () => (position as any[])
      .filter((p) => n(p.value_certified) - n(p.value_billed) > 0.5 && !p.bill_in_progress)
      .sort((a, b) => (n(b.value_certified) - n(b.value_billed))
                    - (n(a.value_certified) - n(a.value_billed))),
    [position])

  const ready = useMemo(
    () => outstanding.filter((p) => !p.cannot_bill_because), [outstanding])
  const shut = useMemo(
    () => outstanding.filter((p) => p.cannot_bill_because), [outstanding])

  const inProgress = (position as any[]).filter((p) => p.bill_in_progress)

  /*
    THE ORDER THAT IS HELD, AND THE BILL HOLDING IT — 0173.

    Until now this page said "N other orders have a bill already open" and
    stopped there. True, and useless: it named neither the order nor the bill,
    so a Project Manager who had raised a draft against the wrong period had no
    way from here to the thing he needed to put down, and the sentence read
    like a rule rather than a task.

    The register is already on this page, so the open bill can simply be looked
    up. Nothing is fetched that was not fetched before.
  */
  const held = useMemo(
    () => inProgress.map((p: any) => ({
      ...p,
      bill: (rows as any[]).find(
        (r) => r.wo_id === p.wo_id
          && ['DRAFT', 'SUBMITTED', 'RETURNED'].includes(String(r.status))),
    })),
    [position, rows])

  const shown = useMemo(() => {
    const t = f.term.trim().toLowerCase()
    return sortRows(rows.filter((r) => {
      if (f.status && r.status !== f.status) return false
      if (f.site && r.site_id !== f.site) return false
      if (t && ![r.id, r.wo_id, r.vendor_name, r.site_name, r.wo_title]
        .some((v) => String(v ?? '').toLowerCase().includes(t))) return false
      return true
    }), sort, {
      gross_value: 'number', net_payable: 'number', cum_value: 'number',
      date_created: 'date', ra_no: 'number',
    })
  }, [rows, f, sort])

  const siteOptions = useMemo(
    () => [...new Set(rows.map((r) => r.site_id))]
      .map((id) => ({
        value: id,
        label: (allSites as any[]).find((s) => s.id === id)?.site_name ?? id,
      })).sort((a, b) => a.label.localeCompare(b.label)),
    [rows, allSites])

  async function prepare(woId: string) {
    setBusy(true)
    try {
      const { data, error } = await supabase.rpc('rpc_ra_prepare', { p_wo_id: woId })
      if (error) throw new Error(error.message)
      toast.success(`${data} prepared from the Measurement Book.`)
      refresh(); refreshPos()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not prepare a bill')
    } finally { setBusy(false) }
  }

  if (loading) return <Loading label="Reading the running account bills…" />

  const awaiting = rows.filter((r) => r.status === 'SUBMITTED').length
  const unpaid = rows.filter((r) => r.status === 'APPROVED'
    && r.payable_status !== 'PAID').reduce((a, r) => a + n(r.net_payable), 0)
  const retentionHeld = (position as any[]).reduce((a, p) => a + n(p.retention_held), 0)

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-slate-900">RA Bills</h1>
        <p className="text-sm text-slate-500">
          Running account bills against works contracts. Each one is everything certified
          to date, less every earlier bill.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Ready to bill" value={ready.length}
              sub="orders with certified work not yet on a bill"
              tone={ready.length ? 'warn' : 'default'}
              icon={<Ruler className="h-4 w-4" />} />
        <Stat label="Awaiting approval" value={awaiting}
              sub="with the PM, the Project Head or the CMD"
              icon={<FileSignature className="h-4 w-4" />} />
        <Stat label="Approved, not yet paid" value={rupees(unpaid)}
              sub="payables raised and open"
              icon={<Wallet className="h-4 w-4" />} />
        <Stat label="Retention held" value={rupees(retentionHeld)}
              sub="across every live contract"
              icon={<Scale className="h-4 w-4" />} />
      </div>

      {/* ------------------------------------------------------------------
          NOTHING IS READY, SAID OUT LOUD.

          This panel used to render only when something was ready, so a
          perfectly correct empty state was indistinguishable from a broken
          screen. It cost an hour on 20-09-2026.
      ------------------------------------------------------------------ */}
      {canBill && ready.length === 0 && shut.length === 0 && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
          <Calculator className="mr-1 inline h-3.5 w-3.5" />
          <strong>Nothing is ready to bill.</strong> Every order has been billed for all the
          work certified on it. A running account bill is prepared from certified
          measurements — get the Measurement Book signed off and the order will appear here
          with a button on it.
          {/* 0173: and if an open bill is the reason the list is empty, the panel below
              names it and links to it, rather than mentioning it in half a clause. */}
          {held.length > 0 && <HeldByAnOpenBill held={held} />}
        </div>
      )}

      {/* ---------- what is waiting to be billed ---------- */}
      {canBill && (ready.length > 0 || shut.length > 0) && (
        <Card title="Certified, and not yet on a bill"
              subtitle="Work the Resident Construction Manager has signed for that nobody has billed. Preparing a bill reads these figures straight out of the Measurement Book.">
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Work order</th><th>Site</th><th>Contractor</th>
                  <th className="num">Certified</th>
                  <th className="num">Billed so far</th>
                  <th className="num">Ready to bill</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {outstanding.map((p: any) => (
                  <tr key={p.wo_id} className={p.cannot_bill_because ? 'bg-slate-50/70' : ''}>
                    <td className="whitespace-nowrap">
                      <Link to={`/service/work-orders/${p.wo_id}`}
                            className="font-medium text-amber-700 hover:underline">{p.wo_id}</Link>
                      <span className="block max-w-[200px] truncate text-[11px] text-slate-400">
                        {p.title}
                      </span>
                    </td>
                    <td className="max-w-[140px] truncate">{p.site_name}</td>
                    <td className="max-w-[160px] truncate">{p.vendor_name}</td>
                    <td className="num whitespace-nowrap">{rupees(p.value_certified)}</td>
                    <td className="num whitespace-nowrap">{rupees(p.value_billed)}</td>
                    <td className={`num whitespace-nowrap font-bold ${
                      p.cannot_bill_because ? 'text-slate-500' : 'text-emerald-700'}`}>
                      {rupees(n(p.value_certified) - n(p.value_billed))}
                    </td>
                    <td>
                      {p.cannot_bill_because ? (
                        // No button, and no silence either. The sentence is the
                        // one the database would have refused with.
                        <span className="flex max-w-[320px] items-start gap-1.5 text-[11px]
                                         leading-snug text-slate-500">
                          <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                          {p.cannot_bill_because}
                        </span>
                      ) : (
                        <button className="btn-primary btn-sm" disabled={busy}
                                onClick={() => void prepare(p.wo_id)}>
                          <Calculator className="h-4 w-4" />
                          Prepare RA-{n(p.last_ra_no) + 1}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {held.length > 0 && <HeldByAnOpenBill held={held} />}
        </Card>
      )}

      <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
        <TrendingUp className="mr-1 inline h-3.5 w-3.5" />
        <strong>Cumulative, always.</strong> RA-2 reads &ldquo;certified to date&rdquo; and
        subtracts RA-1, so a deduction certified after RA-1 went out simply makes RA-2
        smaller — no credit note, no reversal, and the running total is right at every point
        in the contract&rsquo;s life. A bill <strong>can</strong> come out negative, and when
        it does that is the mechanism working.
      </div>

      <Card title={null}>
        <div className="mb-3 flex flex-wrap gap-2">
          <SearchInput value={f.term} onChange={(v) => set({ term: v })}
                       placeholder="Bill, work order, contractor or site…"
                       className="min-w-[220px] flex-1" />
          <Select value={f.status} onChange={(e) => set({ status: e.target.value })}
                  className="w-44" placeholder="Any status"
                  options={[...new Set(rows.map((r) => r.status))].sort()
                    .map((s) => ({ value: s, label: s }))} />
          <Select value={f.site} onChange={(e) => set({ site: e.target.value })}
                  className="w-48" placeholder="Every site" options={siteOptions} />
          {dirty && (
            <button className="btn-ghost text-slate-600" onClick={() => setF({ ...BLANK })}>
              <FilterX className="h-4 w-4" /> Clear
            </button>
          )}
        </div>

        {shown.length === 0 ? (
          <EmptyState icon={<Receipt className="h-8 w-8" />}
                      title={dirty ? 'Nothing matches' : 'No bills yet'}
                      message={dirty
                        ? `${rows.length} bill(s) are hidden by the filters.`
                        : 'A bill is prepared from certified measurements — the panel above lists what is ready.'} />
        ) : (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <Th id="id" sort={sort} onSort={toggle}>Bill</Th>
                  <Th id="wo_id" sort={sort} onSort={toggle}>Work order</Th>
                  <th>Contractor</th>
                  <Th id="cum_value" sort={sort} onSort={toggle} align="right">Certified to date</Th>
                  <Th id="gross_value" sort={sort} onSort={toggle} align="right">This bill</Th>
                  <th className="num">Deductions</th>
                  <Th id="net_payable" sort={sort} onSort={toggle} align="right">Net payable</Th>
                  <Th id="status" sort={sort} onSort={toggle}>Status</Th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => {
                  const ded = n(r.retention_amount) + n(r.tds_amount)
                    + n(r.advance_recovered) + n(r.other_deductions)
                  return (
                    <tr key={r.id}>
                      <td className="whitespace-nowrap font-medium">
                        <Link to={`/service/ra-bills/${r.id}`}
                              className="text-amber-700 hover:underline">{r.id}</Link>
                        <span className="block text-[11px] text-slate-400">
                          RA-{r.ra_no}{r.is_final && ' · FINAL'} · {ddmmyyyy(r.bill_date)}
                        </span>
                      </td>
                      <td className="whitespace-nowrap">
                        <Link to={`/service/work-orders/${r.wo_id}`}
                              className="text-amber-700 hover:underline">{r.wo_id}</Link>
                        {r.pct_of_contract != null && (
                          <span className="block text-[11px] text-slate-400">
                            {r.pct_of_contract}% of contract
                          </span>
                        )}
                      </td>
                      <td className="max-w-[160px] truncate">
                        {r.vendor_name}
                        {r.is_reverse_charge && <span className="pill-grey ml-1">RCM</span>}
                      </td>
                      <td className="num whitespace-nowrap">{rupees(r.cum_value)}</td>
                      <td className={n(r.gross_value) < 0
                        ? 'num whitespace-nowrap font-semibold text-red-700'
                        : 'num whitespace-nowrap font-semibold'}>
                        {rupees(r.gross_value)}
                      </td>
                      <td className="num whitespace-nowrap text-xs text-slate-500">
                        {rupees(ded)}
                      </td>
                      <td className={n(r.net_payable) < 0
                        ? 'num whitespace-nowrap font-bold text-red-700'
                        : 'num whitespace-nowrap font-bold'}>
                        {rupees(r.net_payable)}
                      </td>
                      <td className="whitespace-nowrap">
                        <StatusPill status={r.status} />
                        {r.payable_status && (
                          <span className="block text-[11px] text-slate-400">
                            payable {r.payable_status.toLowerCase()}
                          </span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}

// ---------------------------------------------------------------------------
// ONE BILL
// ---------------------------------------------------------------------------
export function RaBillDetail() {
  const { id = '' } = useParams()
  const { profile } = useAuth()
  const people = usePeople()
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  // Its own flag, not `busy`. Printing takes a second or two and must not grey
  // out Submit while it runs — they are unrelated actions.
  const [pdfBusy, setPdfBusy] = useState(false)
  // 0173. A draft bill holds the order's ONE open slot, and until this existed
  // there was no way to give the slot back.
  const [withdrawing, setWithdrawing] = useState(false)
  const [why, setWhy] = useState('')

  const { data: rows, loading, refresh } = useQuery<RaRow>(
    `ra_${id}`,
    () => supabase.from('v_ra_bill_register').select('*').eq('id', id),
    [id], { cacheable: false })

  const { data: lines } = useQuery<RaLine>(
    `ra_lines_${id}`,
    () => supabase.from('wo_ra_bill_lines').select('*').eq('ra_id', id).order('sl_no'),
    [id], { cacheable: false })

  const { data: siteRow } = useQuery<any>(
    `ra_site_${id}`,
    () => supabase.from('sites').select('id, project_manager_id')
      .eq('id', rows[0]?.site_id ?? '__none__'),
    [rows[0]?.site_id], { cacheable: false, enabled: Boolean(rows[0]?.site_id) })

  /**
   * WHAT THE ORDER IS WORTH NOW — added 17-09-2026.
   *
   * v_ra_bill_register measures "per cent certified" against contract_value,
   * which is the value as SIGNED and, since 0135, never moves. On an order that
   * has been varied upwards that reads over 100% and looks like an over-run
   * when the variation is precisely what made it lawful. The signed figure is
   * still the right one for the advance - it was paid against that number - so
   * both are shown, and the percentage is measured against whichever is the
   * real ceiling today.
   */
  const { data: vpos } = useQuery<any>(
    `ra_vpos_${id}`,
    () => supabase.from('v_wo_variation_position').select('*')
      .eq('wo_id', rows[0]?.wo_id ?? '__none__'),
    [rows[0]?.wo_id], { cacheable: false, idField: 'wo_id',
                        enabled: Boolean(rows[0]?.wo_id) })

  if (loading) return <Loading label="Reading the bill…" />
  const b = rows[0]
  if (!b) {
    return <EmptyState icon={<Receipt className="h-8 w-8" />} title="Not found"
                       message="No such bill, or it belongs to a site you cannot see." />
  }

  // Board 16-09-2026: the site's Project Manager or the Project Head raise and
  // submit a bill. Recording that money left is wider - the person who pays is
  // not the person who bills.
  const canBill = serviceCan(profile?.role, 'prepareRaBill')
  const canPay = serviceCan(profile?.role, 'recordPayment')
  const negative = n(b.gross_value) < 0

  /*
    WHO MAY PUT A BILL DOWN — 0173, board 22-09-2026.

    The man who prepared it, plus Accounts and the administrator. Exactly the
    rule the advance, the retention release and the credit note already use, so
    this is five documents with one rule rather than five with four. The
    database enforces it as well; this only decides whether to draw the button.

    DRAFT and RETURNED only. Once it is SUBMITTED it is the approver's to
    return or reject, so that his reason goes on the record instead of the bill
    quietly leaving his queue.
  */
  const isUnsent = ['DRAFT', 'RETURNED'].includes(b.status)
  const iPreparedIt = !!b.created_by && b.created_by === profile?.id
  const canWithdraw = isUnsent
    && (iPreparedIt || ['admin', 'accounts_spoc'].includes(profile?.role ?? ''))

  async function withdraw() {
    setBusy(true)
    try {
      const { error } = await supabase.rpc('rpc_ra_withdraw',
        { p_ra_id: b.id, p_reason: why.trim() })
      if (error) throw new Error(error.message)
      setWithdrawing(false); setWhy('')
      toast.success(`Withdrawn. ${b.wo_id} can be billed again.`)
      refresh()
    } catch (e) { toast.error(dbMessage(e, 'Could not withdraw it')) }
    finally { setBusy(false) }
  }

  async function submit() {
    setBusy(true)
    try {
      const { data, error } = await supabase.rpc('rpc_works_action',
        { p_doc_type: 'RA', p_doc_id: b.id, p_action: 'SUBMIT' })
      if (error) throw new Error(error.message)
      toast.success(String(data)); refresh()
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Could not submit it') }
    finally { setBusy(false) }
  }

  /**
   * THE PAPER THE CONTRACTOR IS HANDED FOR THE MONEY.
   *
   * Until this existed he got a payment and no statement of it. Between the
   * work he did and the figure that reached his bank sit six movements — the
   * cumulative frame, GST, retention, TDS and advance recovery — and the only
   * place they were explained was a screen he cannot log in to.
   *
   * Not role-gated, on the same reasoning as the work order's and the
   * variation's: if a person may read this bill he may print it. The STATUS is
   * the control — the certificate carries the Project Head's seal and the words
   * "certified for payment", so the edge function refuses anything that has not
   * cleared the chain, and the button is not drawn for those statuses either.
   */
  async function downloadPdf() {
    setPdfBusy(true)
    try {
      await downloadFromFunction('generate-ra-pdf', { ra_id: id }, `${id}.pdf`)
      toast.success('Payment certificate downloaded.')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not generate the certificate')
    } finally { setPdfBusy(false) }
  }

  const D = ({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) => (
    <div>
      <dt className="text-xs font-medium text-slate-500">{label}</dt>
      <dd className="mt-0.5 break-words text-sm text-slate-800">{value ?? '—'}</dd>
      {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
    </div>
  )

  /** One row of the money ladder. */
  const M = ({ label, amount, sub, tone, strong }: {
    label: string; amount: number; sub?: string
    tone?: 'add' | 'less' | 'net'; strong?: boolean
  }) => (
    <div className={tone === 'net'
      ? 'flex flex-wrap items-baseline justify-between gap-2 border-t-2 border-slate-300 pt-2'
      : 'flex flex-wrap items-baseline justify-between gap-2'}>
      <div className="min-w-0">
        <p className={strong ? 'text-sm font-semibold text-slate-800' : 'text-sm text-slate-600'}>
          {tone === 'less' && <span className="mr-1 text-red-500">less</span>}
          {label}
        </p>
        {sub && <p className="text-[11px] text-slate-400">{sub}</p>}
      </div>
      <p className={
        tone === 'net' ? 'text-lg font-bold tabular-nums text-slate-900'
          : tone === 'less' ? 'tabular-nums text-red-700'
          : strong ? 'font-semibold tabular-nums text-slate-800'
          : 'tabular-nums text-slate-700'}>
        {tone === 'less' && amount !== 0 ? '− ' : ''}{rupees(Math.abs(amount))}
      </p>
    </div>
  )

  return (
    <div className="space-y-4">
      <Link to="/service/ra-bills"
            className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="h-4 w-4" /> All RA bills
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-slate-900">
            {b.id} <span className="text-base font-semibold text-slate-500">· RA-{b.ra_no}</span>
          </h1>
          <p className="text-sm text-slate-600">{b.wo_title}</p>
          <p className="mt-1 text-xs text-slate-500">
            {b.site_name} · <Link to={`/service/work-orders/${b.wo_id}`}
                                  className="text-amber-700 hover:underline">{b.wo_id}</Link>
            {' '}· {b.vendor_name}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill status={b.status} />
          {b.status === 'APPROVED' && (
            <button className="btn-secondary btn-sm" disabled={pdfBusy}
                    onClick={() => void downloadPdf()}>
              <FileDown className="h-4 w-4" />
              {pdfBusy ? 'Preparing…' : 'Payment certificate'}
            </button>
          )}
          {canWithdraw && (
            <button className="btn-secondary btn-sm" disabled={busy}
                    title={`Put this bill down so ${b.wo_id} can be billed again`}
                    onClick={() => { setWhy(''); setWithdrawing(true) }}>
              <Trash2 className="h-4 w-4" /> Withdraw
            </button>
          )}
          {isUnsent && canBill && (
            <button className="btn-primary btn-sm" disabled={busy} onClick={() => void submit()}>
              Submit for approval
            </button>
          )}
        </div>
      </div>

      {negative && (
        <Alert tone="warning" title="This bill is negative, and that is the mechanism working">
          More was billed on earlier bills than is now certified — usually because a deduction
          was certified in the Measurement Book after the last bill went out. The cumulative
          design corrects it here instead of needing a credit note against RA-{b.ra_no - 1}.
          {n(b.net_payable) < 0 && <> Nothing is paid on this bill; it reduces what the next one
          claims. No payable is raised for a negative amount.</>}
        </Alert>
      )}

      {b.status === 'RETURNED' && b.return_reason && (
        <Alert tone="warning" title="Returned for revision">{b.return_reason}</Alert>
      )}
      {b.status === 'APPROVED' && b.payable_id && (
        <Alert tone="success" title="Approved — the payable is raised">
          {/*
            The payable itself, not the register it sits in. Reported
            20-09-2026: the link printed a document number and opened a list
            of a hundred, which is a promise the link does not keep.
          */}
          <Link to={`/service/contractor-payables/${b.payable_id}`}
                className="font-medium underline">
            {b.payable_id}
          </Link>{' '}
          for {rupees(b.net_payable)} is open in <strong>Contractor Payables</strong>. Record
          the payment and attach the bank advice at the foot of this page.
        </Alert>
      )}

      <ApprovalChain docType="RA" docId={b.id} status={b.status}
                     currentStep={b.current_step}
                     sitePmId={siteRow[0]?.project_manager_id}
                     createdBy={b.created_by}
                     onChanged={refresh} />

      {/* ----------------------------------------------------------------
          IS THIS THE LAST ONE? — 0157.

          wo_ra_bills.is_final existed from the day the RA bill was built and
          nothing ever wrote to it. Every bill in the database carried false,
          so rpc_wo_close could never be satisfied and the only way out of a
          work order was a short close — which records in the audit trail that
          the contract was abandoned. Orders that finished perfectly well
          would have been written up as failures.

          It sits here rather than on the bill form because in this business
          you usually do not know. The site raises RA-4 in October believing
          RA-5 is coming; in January the scope changes and RA-4 turns out to
          have been the last. Finality is decided looking back.
      ---------------------------------------------------------------- */}
      {b.status === 'APPROVED' && <RaFinalBill bill={b} onChanged={refresh} />}

      {/* ----------------------------------------------------------------
          CREDIT NOTES — 0165. On the bill rather than on a screen of their
          own, because the two figures that decide whether one is even
          possible — what the bill was certified at, and what has already
          been paid against it — are both here and nowhere else.
      ---------------------------------------------------------------- */}
      {b.status === 'APPROVED' && (
        <RaiseCreditNote
          bill={{ id: b.id, ra_no: b.ra_no, net_payable: b.net_payable,
                  paid_to_date: b.paid_to_date, wo_id: b.wo_id }}
          onDone={refresh} />
      )}

      {/* ---------- where this bill sits in the contract ---------- */}
      <Card title="Where this bill sits"
            subtitle="A running account bill is everything certified to date, less every earlier bill.">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {(() => {
            const varied = n(vpos[0]?.variation_value)
            const ceiling = varied !== 0 ? n(vpos[0]?.revised_value) : n(b.contract_value)
            const pct = ceiling > 0
              ? Math.round((n(b.cum_value) / ceiling) * 1000) / 10
              : null
            return (
              <Stat label={varied !== 0 ? 'Contract value, with variations' : 'Contract value'}
                    value={rupees(ceiling)}
                    sub={<>
                      {pct != null && <>{pct}% certified so far</>}
                      {varied !== 0 && (
                        <span className="block text-[11px] text-slate-400">
                          {rupees(b.contract_value)} as signed,
                          {' '}{varied < 0 ? '−' : '+'}{rupees(Math.abs(varied))} by variation
                        </span>
                      )}
                    </>} />
            )
          })()}
          <Stat label="Certified to date" value={rupees(b.cum_value)} sub="cumulative, at contract rates" />
          <Stat label="Earlier bills" value={rupees(b.prev_value)}
                sub={b.ra_no > 1 ? `RA-1 to RA-${b.ra_no - 1}` : 'this is the first bill'} />
          <Stat label="This bill, gross" value={rupees(b.gross_value)}
                tone={negative ? 'bad' : 'good'} sub="certified to date less earlier bills" />
        </div>
      </Card>

      {/* ---------- the lines ---------- */}
      <Card title="Line by line"
            subtitle="Every quantity read from certified measurement pages. Nothing here was typed.">
        {/*
          A table that shows nothing must say so. On 15-09-2026 this rendered a
          header and no rows, and there was no way to tell from the screen
          whether the bill genuinely had no lines or whether the read had
          failed - the figures above came from the bill header and looked fine
          either way. An empty table is now a sentence.
        */}
        {lines.length === 0 ? (
          <p className="rounded-lg border border-dashed border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <strong>No lines came back for this bill.</strong> The totals above are on the
            bill header, so if they are not zero the lines exist and could not be read —
            reload the page, and if it persists tell the System Administrator. A bill is
            never prepared without at least one line.
          </p>
        ) : (
        <div className="overflow-x-auto">
          <table className="tbl">
            <thead>
              <tr>
                <th>#</th><th>Description</th><th>UOM</th>
                <th className="num">Rate</th>
                <th className="num">Certified to date</th>
                <th className="num">On earlier bills</th>
                <th className="num">This bill</th>
                <th className="num">Value</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l.id} className={n(l.qty_this) < 0 ? 'bg-red-50/50' : undefined}>
                  <td>{l.sl_no}</td>
                  <td className="max-w-[260px]">{l.description}</td>
                  <td>{l.uom}</td>
                  <td className="num">{rupees(l.rate)}</td>
                  <td className="num">{qty(l.qty_cum)}</td>
                  <td className="num text-slate-500">{qty(l.qty_prev)}</td>
                  <td className={n(l.qty_this) < 0
                    ? 'num font-semibold text-red-700' : 'num font-semibold'}>
                    {qty(l.qty_this)}
                  </td>
                  <td className={n(l.value_this) < 0
                    ? 'num whitespace-nowrap font-semibold text-red-700'
                    : 'num whitespace-nowrap font-semibold'}>
                    {rupees(l.value_this)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </Card>

      {/* ---------- the money ladder ---------- */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="What he is paid"
              subtitle="Every figure below is derived — from the Measurement Book, the terms agreed on the work order, and his PAN.">
          <div className="space-y-2.5">
            <M label="Work certified this bill" amount={n(b.gross_value)} strong />

            {b.is_reverse_charge ? (
              <div className="rounded-lg bg-amber-50 px-3 py-2">
                <p className="flex items-start gap-1.5 text-xs text-amber-900">
                  <Landmark className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>
                    <strong>Reverse charge.</strong>{' '}
                    <strong>OVAL pays {rupees(b.rcm_gst_amount)} to the Government
                    directly.</strong> It is not added to his bill and not deducted from
                    him — it never touches what he is paid.{' '}
                    {rcmSupplierClause({
                      registered: b.is_gst_registered,
                      notification: b.rcm_notification,
                      entryNo: b.rcm_entry_no,
                    })}
                    {b.is_gst_registered && (
                      <span className="mt-1 block">
                        OVAL does <strong>not</strong> raise a self-invoice for this supply —
                        his own invoice is the document.
                      </span>
                    )}
                  </span>
                </p>
              </div>
            ) : (
              <M label={`GST at ${n(b.gst_rate_percent)}%`} amount={n(b.gst_amount)} tone="add"
                 sub={`${b.gst_treatment} · he charges it, OVAL claims the input credit`} />
            )}

            <M label={`Retention at ${n(b.retention_pct)}%`} amount={n(b.retention_amount)}
               tone="less"
               sub={`${rupees(b.cum_retention)} held on this contract in all, released after the defect liability period`} />

            <M label={`TDS at ${b.tds_pct == null ? '—' : n(b.tds_pct)}%`}
               amount={n(b.tds_amount)} tone="less"
               sub={b.tds_pct == null
                 ? 'no valid PAN on file'
                 : `under ${b.tds_section} · on the work value, NOT on the GST`} />

            {n(b.advance_recovered) !== 0 && (
              <M label="Mobilisation advance recovered" amount={n(b.advance_recovered)}
                 tone="less"
                 sub={`${rupees(b.advance_outstanding)} of advance still outstanding after this bill`} />
            )}

            {n(b.other_deductions) !== 0 && (
              <M label={b.other_deduction_note || 'Other deductions'}
                 amount={n(b.other_deductions)} tone="less" />
            )}

            <M label="Net payable" amount={n(b.net_payable)} tone="net" />
          </div>

          {b.tds_pct == null && (
            <p className="mt-3 flex items-start gap-1.5 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-900">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                <strong>No valid PAN on file for {b.vendor_name}</strong>, so no TDS has been
                worked out. Without a PAN the statutory rate is 20%, and somebody has to decide
                that deliberately before this bill is approved.
              </span>
            </p>
          )}
        </Card>

        <Card title="The bill" subtitle="What an auditor will ask for.">
          <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
            <D label="Bill number" value={`${b.id} · RA-${b.ra_no}`} />
            <D label="Bill date" value={ddmmyyyy(b.bill_date)} />
            <D label="Period" value={b.period_from && b.period_to
              ? `${ddmmyyyy(b.period_from)} to ${ddmmyyyy(b.period_to)}`
              : 'not stated'} />
            <D label="Contractor" value={<>{b.vendor_name}<br />
              <span className="text-xs text-slate-500">PAN {b.vendor_pan ?? 'none on file'}</span></>} />
            <D label="Prepared by" value={people.name({ id: b.created_by, email: b.creator_email })} />
            <D label="Approved by" value={b.approver_email
              ? people.name({ email: b.approver_email }) : '—'} />
            <D label="Retention held to date" value={rupees(b.cum_retention)}
               hint="on this contract, across every bill" />
            {/*
              ALWAYS DRAWN, even before Accounts has a payable to point at.
              A figure that will appear later should hold its place; a field
              that simply is not there reads as a field somebody forgot.
            */}
            <D label="Payable"
               value={b.payable_id
                 ? <>{b.payable_id}
                     {b.payable_status && <span className="pill-grey ml-1">{b.payable_status}</span>}</>
                 : <span className="text-slate-400">not raised yet</span>}
               hint={b.payable_id
                 ? (n(b.paid_to_date) > 0 ? `${rupees(b.paid_to_date)} paid` : 'nothing paid yet')
                 : 'raised automatically when the bill is approved'} />
          </dl>

          {/*
            THE MOBILISATION ADVANCE, IN FULL.
            Until 16-09-2026 this screen showed only what was recovered and what
            was left, so the amount he was actually advanced — the figure the
            whole recovery is a fraction of — appeared nowhere and had to be
            inferred. An advance recovery statement that does not state the
            advance is not a statement.
          */}
          {n(b.advance_paid) > 0 && (
            <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
                Mobilisation advance
              </p>
              <dl className="mt-1.5 space-y-1 text-sm">
                <div className="flex justify-between gap-3">
                  <dt className="text-slate-600">
                    Advance paid
                    <span className="ml-1 text-[11px] text-slate-400">
                      {n(b.advance_pct)}% of {rupees(b.contract_value)}
                    </span>
                  </dt>
                  <dd className="font-semibold tabular-nums text-slate-800">
                    {rupees(b.advance_paid)}
                  </dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-slate-600">Recovered on earlier bills</dt>
                  <dd className="tabular-nums text-slate-700">
                    {rupees(n(b.cum_advance_recovered) - n(b.advance_recovered))}
                  </dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-slate-600">
                    Recovered on this bill
                    <span className="ml-1 text-[11px] text-slate-400">
                      {n(b.advance_recovery_pct)}% of the bill
                    </span>
                  </dt>
                  <dd className="tabular-nums text-slate-700">{rupees(b.advance_recovered)}</dd>
                </div>
                <div className="flex justify-between gap-3 border-t border-slate-200 pt-1">
                  <dt className="font-semibold text-slate-700">Still outstanding</dt>
                  <dd className="font-bold tabular-nums text-slate-900">
                    {rupees(b.advance_outstanding)}
                  </dd>
                </div>
              </dl>
            </div>
          )}
          {b.remarks && (
            <p className="mt-3 rounded bg-slate-50 px-3 py-2 text-xs text-slate-600">{b.remarks}</p>
          )}
          <p className="mt-3 flex items-start gap-1.5 text-[11px] text-slate-400">
            <Banknote className="mt-0.5 h-3 w-3 shrink-0" />
            An approved bill raises a payable in the same ledger every material purchase uses,
            at the net figure — the amount that actually leaves the bank. Retention and TDS are
            recorded here and reported in the statutory pack.
          </p>
        </Card>
      </div>

      {/* Payment proof. Only once the chain has cleared — nothing is paid
          against a bill that has not been approved, and the RPC says so too. */}
      {b.status === 'APPROVED' && (
        <RaPayments raBillId={b.id} label={b.id}
                    netPayable={n(b.net_payable)} paidToDate={n(b.paid_to_date)}
                    canPay={canPay} onChanged={refresh} />
      )}

      {/* His bill, coming the other way. BELOW the payments on purpose: the
          order on the page is the order of events — OVAL certifies, OVAL pays,
          and his invoice arrives somewhere in between or afterwards. The card
          draws nothing until the bill is approved. */}
      <ContractorInvoices raBillId={b.id} status={b.status} onChanged={refresh} />

      <AuditTrail docType="RA" docId={b.id} />

      {/* ------------------------------------------------------------------
          WITHDRAWING THE BILL — 0173.

          The words matter more than the button. A bill number with no money
          against it is the first thing an auditor asks about, so the reason
          typed here is not a formality: it is the only sentence that will ever
          explain the gap. The modal says that, and says what withdrawal does
          NOT do, because "cancel" reads like "delete" to everybody who has
          used any other system.
      ------------------------------------------------------------------ */}
      <Modal open={withdrawing} onClose={() => setWithdrawing(false)}
             title="Withdraw this bill"
             subtitle={`${b.id} · RA-${b.ra_no} on ${b.wo_id}`}
             footer={
               <>
                 <button className="btn-secondary" disabled={busy}
                         onClick={() => setWithdrawing(false)}>
                   Keep it
                 </button>
                 <button className="btn-warning"
                         disabled={busy || why.trim().length < 10}
                         onClick={() => void withdraw()}>
                   {busy ? 'Withdrawing…' : 'Withdraw it'}
                 </button>
               </>
             }>
        <div className="space-y-3">
          <Alert tone="info" title="Nothing is deleted, and nothing is unwound">
            RA-{b.ra_no} stays on the register marked withdrawn, with its lines and every
            figure on it. It was never approved, so it has moved no quantity, raised no
            payable and recovered no advance — there is nothing to reverse.{' '}
            <strong>{b.wo_id} can be billed again the moment you do this</strong>, because a
            withdrawn bill no longer holds the one open slot on the order.
          </Alert>
          <Field label="Why is it being withdrawn"
                 hint="At least a short sentence. RA numbers run in sequence, so this is the only thing that will ever explain why this one has no money against it.">
            <Textarea rows={3} value={why} onChange={(e) => setWhy(e.target.value)}
                      placeholder="e.g. Raised against the wrong period — the measurement it was built on belongs to next month." />
          </Field>
          {why.trim().length > 0 && why.trim().length < 10 && (
            <p className="text-xs text-amber-700">
              A few more words — {10 - why.trim().length} to go.
            </p>
          )}
        </div>
      </Modal>
    </div>
  )
}

// ---------------------------------------------------------------------------
// WHAT HAS ACTUALLY BEEN PAID — moved out, 16-09-2026
//
// The payments card lived here until the contractor payable got a detail screen
// of its own. Two screens showing the same payments meant one copy or two, and
// two copies is how a modal gains a field on one screen only. It is now
// src/service/components/RaPayments.tsx and both screens import it.
// ---------------------------------------------------------------------------


/* ========================================================================== */
/*  DECLARING THE FINAL BILL                                                  */
/* ========================================================================== */

/** Mirrors may_close_wo(). The RPC is the enforcement; this is the door. */
const MAY_DECLARE_FINAL = ['admin', 'cmd', 'project_head', 'procurement_head']

function RaFinalBill({ bill, onChanged }: { bill: RaRow; onChanged: () => void }) {
  const { profile } = useAuth()
  const toast = useToast()
  const [asking, setAsking] = useState<'SET' | 'UNSET' | null>(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  const mayDeclare = MAY_DECLARE_FINAL.includes(profile?.role ?? '')

  // Only the last approved bill on the order can be the final one. The RPC
  // refuses otherwise; asking here saves the user a pointless refusal.
  const { data: siblings } = useQuery<{ ra_no: number; status: string }>(
    `ra_siblings_${bill.wo_id}`,
    () => supabase.from('v_ra_bill_register').select('ra_no, status')
      .eq('wo_id', bill.wo_id),
    [bill.wo_id, bill.status], { cacheable: false, idField: 'ra_no' })

  const highest = siblings
    .filter((r) => r.status !== 'REJECTED')
    .reduce((a, r) => Math.max(a, Number(r.ra_no) || 0), 0)
  const isLast = highest === 0 || Number(bill.ra_no) >= highest

  const act = async (final: boolean) => {
    setBusy(true)
    const { error } = await supabase.rpc('rpc_ra_mark_final', {
      p_ra_id: bill.id, p_final: final, p_reason: reason.trim() || null,
    })
    setBusy(false)
    if (error) { toast.error(dbMessage(error, 'Could not change the declaration.')); return }
    setAsking(null); setReason('')
    onChanged()
    toast.success(final ? 'Declared the final bill.' : 'Declaration withdrawn.')
  }

  if (bill.is_final) {
    return (
      <>
        <Card title="This is the final bill">
          <div className="flex items-start gap-3">
            <Flag className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
            <div className="flex-1 space-y-2">
              <p className="text-sm text-slate-700">
                No further running account bill can be raised against{' '}
                <Link to={`/service/work-orders/${bill.wo_id}`}
                      className="font-medium text-amber-700 hover:underline">
                  {bill.wo_id}
                </Link>. The order can now be closed out once every payable is paid.
              </p>
              {mayDeclare && (
                <button className="btn-secondary btn-sm"
                        onClick={() => { setReason(''); setAsking('UNSET') }}>
                  <Undo2 className="h-4 w-4" /> Withdraw the declaration
                </button>
              )}
            </div>
          </div>
        </Card>

        <Modal open={asking === 'UNSET'} onClose={() => setAsking(null)}
               title="Withdraw the final-bill declaration"
               subtitle={`RA-${bill.ra_no} on ${bill.wo_id}`}
               footer={
                 <>
                   <button className="btn-secondary" onClick={() => setAsking(null)} disabled={busy}>
                     Cancel
                   </button>
                   <button className="btn-warning" disabled={busy} onClick={() => void act(false)}>
                     {busy ? 'Withdrawing…' : 'Withdraw it'}
                   </button>
                 </>
               }>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Further bills will be allowed on this order again. Both the declaration and
              this withdrawal stay in the audit trail.
            </p>
            <Field label="Why" hint="Optional, but this is the entry somebody will ask about.">
              <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                        placeholder="Client reinstated the balance scope on 12-10-2026." />
            </Field>
          </div>
        </Modal>
      </>
    )
  }

  if (!mayDeclare || !isLast) return null

  return (
    <>
      <Card title="Is this the last bill on this order?">
        <div className="flex items-start gap-3">
          <Flag className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
          <div className="flex-1 space-y-2">
            <p className="text-sm text-slate-600">
              A work order cannot be closed out until one of its bills is declared the
              final one. Declare it when you know no further work will be billed —
              usually after the last measurement is certified and paid.
            </p>
            <button className="btn-primary btn-sm"
                    onClick={() => { setReason(''); setAsking('SET') }}>
              <Flag className="h-4 w-4" /> Declare this the final bill
            </button>
          </div>
        </div>
      </Card>

      <Modal open={asking === 'SET'} onClose={() => setAsking(null)}
             title="Declare this the final bill"
             subtitle={`RA-${bill.ra_no} on ${bill.wo_id}`}
             footer={
               <>
                 <button className="btn-secondary" onClick={() => setAsking(null)} disabled={busy}>
                   Cancel
                 </button>
                 <button className="btn-primary" disabled={busy} onClick={() => void act(true)}>
                   {busy ? 'Declaring…' : 'Declare it final'}
                 </button>
               </>
             }>
        <div className="space-y-3">
          <Alert tone="info" title="What this does">
            No further running account bill can be raised against {bill.wo_id}. The order
            becomes eligible to be closed out, once every payable on it is paid. You can
            withdraw the declaration later if the work resumes.
          </Alert>
          <Field label="Why" hint="Optional. It goes into the order's permanent record.">
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                      placeholder="Work complete and handed over; no further billing expected." />
          </Field>
        </div>
      </Modal>
    </>
  )
}
